import { useEffect, useRef, useState } from "react";
import {
  Outlet,
  ScrollRestoration,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Header } from "@/components/layout";
import Sidebar from "@/components/layout/Sidebar";
import { useAuthService, useAuthState } from "@/services/auth";
import { TermsAndConditionsModal } from "@/components/auth/TermsAndConditionsModal";
import { AssistantPanel } from "@/components/workspace";
import { useAssistantPanel } from "@/hooks/workspace";

const Layout = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const lastNonAssistantPathRef = useRef<string>("/app");
  const { user, isAuthenticated, isLoading } = useAuthState();
  const authService = useAuthService();
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [isTermsLoading, setIsTermsLoading] = useState(false);

  // Use workspace store for assistant panel state
  const { isOpen: assistantOpen, setOpen: setAssistantOpen } = useAssistantPanel();

  // Check if user has accepted terms
  useEffect(() => {
    if (!isAuthenticated || isLoading || !user) return;

    // Check if terms are accepted
    const termsAccepted = user.termsAccepted;

    // Check if terms version is up to date
    const currentTermsVersion = import.meta.env["VITE_TERMS_VERSION"];
    const userTermsVersion = user.termsVersion;

    // Logic: Show modal if:
    // 1. Terms not accepted at all
    // 2. Terms accepted but version is outdated (userVersion < currentVersion)
    // Note: String comparison works for simple versions like "1.0" < "1.1"
    // For complex semantic versioning, a helper function would be needed.
    const isVersionOutdated =
      termsAccepted &&
      userTermsVersion &&
      userTermsVersion < currentTermsVersion;

    if (!termsAccepted || isVersionOutdated) {
      console.log("[Layout] Terms check failed:", {
        termsAccepted,
        userTermsVersion,
        currentTermsVersion,
        isVersionOutdated,
      });
      setShowTermsModal(true);
    } else {
      setShowTermsModal(false);
    }
  }, [isAuthenticated, isLoading, user]);

  const handleAcceptTerms = async () => {
    if (authService.acceptTerms) {
      setIsTermsLoading(true);
      try {
        await authService.acceptTerms();
        setShowTermsModal(false);
      } catch (error) {
        console.error("Failed to accept terms:", error);
        // Optionally show an error notification here
      } finally {
        setIsTermsLoading(false);
      }
    }
  };

  // Track last non-assistant route so we can shrink back to it
  useEffect(() => {
    if (!location.pathname.startsWith("/app/assistant")) {
      lastNonAssistantPathRef.current =
        location.pathname + (location.search || "");
    }
  }, [location.pathname, location.search]);

  // Expose a global event for navbar button to toggle assistant
  useEffect(() => {
    const handler = (e: Event) => {
      const detail =
        (e as CustomEvent<{ open?: boolean; expanded?: boolean }>).detail || {};
      // Don't open panel over full-screen assistant page
      if (location.pathname.startsWith("/app/assistant")) {
        if (typeof detail.open === "boolean" && detail.open === false)
          setAssistantOpen(false);
        return;
      }
      if (typeof detail.open === "boolean") {
        setAssistantOpen(detail.open);
      } else {
        // Toggle - need to read current state
        setAssistantOpen(!assistantOpen);
      }
      // If expanded flag is set, navigate to full page instead
      if (typeof detail.expanded === "boolean" && detail.expanded) {
        setAssistantOpen(false);
        navigate("/app/assistant");
      }
    };
    window.addEventListener("abi:assistant:toggle", handler as EventListener);
    return () =>
      window.removeEventListener(
        "abi:assistant:toggle",
        handler as EventListener
      );
  }, [location.pathname, assistantOpen, setAssistantOpen, navigate]);

  // Shrink event from full-screen assistant page
  useEffect(() => {
    const shrinkHandler = () => {
      const target = lastNonAssistantPathRef.current || "/app";
      if (location.pathname !== target) navigate(target);
      setAssistantOpen(true);
    };
    window.addEventListener(
      "abi:assistant:shrink",
      shrinkHandler as EventListener
    );
    return () =>
      window.removeEventListener(
        "abi:assistant:shrink",
        shrinkHandler as EventListener
      );
  }, [location.pathname, navigate, setAssistantOpen]);

  // Smart open event from sidebar - opens full-screen unless overlay is already visible
  useEffect(() => {
    const smartOpenHandler = () => {
      // If overlay panel is already open, just keep it open (do nothing)
      if (assistantOpen) {
        return;
      }
      // Otherwise, navigate to full-screen assistant
      navigate("/app/assistant");
    };
    window.addEventListener(
      "abi:assistant:open-smart",
      smartOpenHandler as EventListener
    );
    return () =>
      window.removeEventListener(
        "abi:assistant:open-smart",
        smartOpenHandler as EventListener
      );
  }, [assistantOpen, navigate]);

  // Keyboard shortcut: Cmd+K / Ctrl+K to toggle assistant panel
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if user is typing in an input/textarea
      const target = e.target as HTMLElement;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable) {
        return;
      }

      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        // Don't toggle panel on full-screen assistant page
        if (!location.pathname.startsWith("/app/assistant")) {
          setAssistantOpen(!assistantOpen);
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [assistantOpen, setAssistantOpen, location.pathname]);

  // Streaming state is now managed in Zustand store automatically
  // No need for manual sync from AIStreamManager

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setIsMobileMenuOpen(false);
  };

  // Routes that manage their own layout (full-bleed, internal scroll areas)
  // These get overflow-hidden on main so they can control their own scrolling
  const fullBleedRoutes = [
    "/app/social/youtube",
    "/app/assistant",
    "/app/workspace/sheets",
  ];

  const isFullBleedRoute = fullBleedRoutes.some(route => location.pathname.startsWith(route));

  return (
    <div className="min-h-screen bg-gray-900">
      <Header
        onMobileMenuToggle={handleMobileMenuToggle}
        showMobileMenuButton={true}
      />

      <div className="flex" style={{ minHeight: "calc(100vh - 3.5rem)", height: "calc(100vh - 3.5rem)" }}>
        {/* New left sidebar (keeps top navbar intact) */}
        <Sidebar
          isMobileOpen={isMobileMenuOpen}
          onMobileClose={handleMobileMenuClose}
        />
        {/* Main content area with responsive padding */}
        <main className={`flex-1 min-w-0 bg-gray-900 ${isFullBleedRoute ? "h-full overflow-hidden" : "overflow-auto"}`}>
          <ScrollRestoration />
          <div className={isFullBleedRoute ? "h-full" : "p-4 lg:p-6"}>
            <Outlet />
          </div>
        </main>
      </div>

      {/* Assistant Panel - workspace-integrated side panel */}
      <AssistantPanel />

      {/* Mandatory Terms and Conditions Modal */}
      <TermsAndConditionsModal
        isOpen={showTermsModal}
        onClose={() => {}} // Cannot close without accepting
        onAccept={handleAcceptTerms}
        onReject={() => authService.logout()}
        isBlocking={true}
        isLoading={isTermsLoading}
      />
    </div>
  );
};

export default Layout;
